import { useState } from 'preact/hooks';
import { startRegistration, startAuthentication } from '@simplewebauthn/browser';
import { api, ApiError } from '../api';
import { beginSession } from '../state/session';

type Mode = 'choose' | 'passkey-new' | 'email' | 'password-login' | 'password-register';

/**
 * Passkeys first, magic link as the fallback for devices without one.
 *
 * Both flows return the signed-in user inline rather than relying on a follow-up
 * `/api/me` — KV sessions are eventually consistent, so an immediate re-read could still
 * miss the session that was just written.
 */
export function SignInPanel() {
  const [mode, setMode] = useState<Mode>('choose');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const passkeysSupported =
    typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';

  const signInWithPasskey = async () => {
    setBusy(true);
    setError(null);
    try {
      const { handle, options } = await api.passkeyLoginOptions();
      const response = await startAuthentication({ optionsJSON: options as never });
      const { user: signedIn } = await api.passkeyLoginVerify({ handle, response });
      await beginSession(signedIn);
    } catch (err) {
      setError(describe(err, 'Could not sign in with a passkey.'));
    } finally {
      setBusy(false);
    }
  };

  const createPasskey = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { handle, options } = await api.passkeyRegisterOptions({ email });
      const response = await startRegistration({ optionsJSON: options as never });
      const { user: created } = await api.passkeyRegisterVerify({ handle, response });
      await beginSession(created);
    } catch (err) {
      setError(describe(err, 'Could not create a passkey.'));
    } finally {
      setBusy(false);
    }
  };

  const sendMagicLink = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.requestMagicLink(email);
      // In local development the API returns the link instead of mailing it.
      setSent(res.devLink ?? '');
    } catch (err) {
      setError(describe(err, 'Could not send the sign-in link.'));
    } finally {
      setBusy(false);
    }
  };

  const loginWithPassword = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user: signedIn } = await api.passwordLogin({ email, password });
      await beginSession(signedIn);
    } catch (err) {
      setError(describe(err, 'Invalid email or password.'));
    } finally {
      setBusy(false);
    }
  };

  const registerWithPassword = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user: signedIn } = await api.passwordRegister({ email, password, displayName });
      await beginSession(signedIn);
    } catch (err) {
      setError(describe(err, 'Could not create an account.'));
    } finally {
      setBusy(false);
    }
  };

  if (sent !== null) {
    return (
      <div class="panel">
        <h2>Check your email</h2>
        <p>
          If <strong>{email}</strong> has an account — or is ready to make one — a sign-in link is
          on its way. It expires in 15 minutes.
        </p>
        {sent && (
          <p class="muted">
            Development mode: <a href={sent}>use this link</a>
          </p>
        )}
      </div>
    );
  }

  return (
    <div class="panel">
      <h2>Sign in</h2>

      {error && (
        <p class="error" role="alert">
          {error}
        </p>
      )}

      {mode === 'choose' && (
        <div class="stack">
          {passkeysSupported && (
            <button type="button" class="button" onClick={signInWithPasskey} disabled={busy}>
              Sign in with a passkey
            </button>
          )}
          {passkeysSupported && (
            <button
              type="button"
              class="button button--ghost"
              onClick={() => setMode('passkey-new')}
              disabled={busy}
            >
              Create an account with a passkey
            </button>
          )}
          <button
            type="button"
            class="button button--ghost"
            onClick={() => setMode('password-login')}
            disabled={busy}
          >
            Sign in with a password
          </button>
          <button
            type="button"
            class="button button--ghost"
            onClick={() => setMode('password-register')}
            disabled={busy}
          >
            Create an account with a password
          </button>
          <button
            type="button"
            class="button button--ghost"
            onClick={() => setMode('email')}
            disabled={busy}
          >
            Email me a sign-in link
          </button>
          {!passkeysSupported && (
            <p class="muted">This browser does not support passkeys, so password or email sign-in it is.</p>
          )}
        </div>
      )}

      {(mode === 'passkey-new' || mode === 'email') && (
        <form class="stack" onSubmit={mode === 'email' ? sendMagicLink : createPasskey}>
          <label class="field">
            <span>Email address</span>
            <input
              type="email"
              required
              autocomplete="username webauthn"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              placeholder="you@example.com"
            />
          </label>
          <button type="submit" class="button" disabled={busy || !email}>
            {busy ? 'Working…' : mode === 'email' ? 'Send link' : 'Create passkey'}
          </button>
          <button type="button" class="button button--ghost" onClick={() => setMode('choose')}>
            Back
          </button>
        </form>
      )}

      {mode === 'password-login' && (
        <form class="stack" onSubmit={loginWithPassword}>
          <label class="field">
            <span>Email address</span>
            <input
              type="email"
              required
              autocomplete="username"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              placeholder="you@example.com"
            />
          </label>
          <label class="field">
            <span>Password</span>
            <input
              type="password"
              required
              autocomplete="current-password"
              value={password}
              onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
              placeholder="••••••••"
            />
          </label>
          <button type="submit" class="button" disabled={busy || !email || !password}>
            {busy ? 'Working…' : 'Sign in'}
          </button>
          <button type="button" class="button button--ghost" onClick={() => setMode('choose')}>
            Back
          </button>
        </form>
      )}

      {mode === 'password-register' && (
        <form class="stack" onSubmit={registerWithPassword}>
          <label class="field">
            <span>Email address</span>
            <input
              type="email"
              required
              autocomplete="username"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              placeholder="you@example.com"
            />
          </label>
          <label class="field">
            <span>Display name (optional)</span>
            <input
              type="text"
              value={displayName}
              onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)}
              placeholder="Your Name"
            />
          </label>
          <label class="field">
            <span>Password (min 8 characters)</span>
            <input
              type="password"
              required
              autocomplete="new-password"
              value={password}
              onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
              placeholder="••••••••"
            />
          </label>
          <button type="submit" class="button" disabled={busy || !email || password.length < 8}>
            {busy ? 'Working…' : 'Create account'}
          </button>
          <button type="button" class="button button--ghost" onClick={() => setMode('choose')}>
            Back
          </button>
        </form>
      )}
    </div>
  );
}

function describe(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  const name = (err as Error)?.name;
  // The WebAuthn ceremony throws these when the user dismisses the platform sheet.
  if (name === 'NotAllowedError' || name === 'AbortError') return 'Cancelled.';
  if (name === 'InvalidStateError') return 'This device already has a passkey for that account.';
  return (err as Error)?.message ?? fallback;
}
